import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { Dialog, DialogActions, DialogTitle } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupIcon from "@mui/icons-material/Group";
import useSnackbar from "../../hooks/useSnackbar";
import { MatrixProvider } from "../../components/matrix/MatrixProvider";
import MatrixChatView from "../../components/matrix/MatrixChatView";
import AddUserToChatModalMatrix from "./AddUserToChatModalMatrix";
import { matrixClient } from "../../services/matrixClient";

/**
 * ClubChatMatrix — Matrix chat page
 *
 * Uses MatrixChatView component for real-time messaging.
 * Supports direct Synapse mode and backend proxy mode.
 */

export default function ClubChatMatrix() {
	const [loading, setLoading] = useState(true);
	const [chat, setChat] = useState<any>();
	const { chatId, clubId } = useParams();
	const [open, setOpen] = useState(false);
	const [addUserOpen, setAddUserOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const openMenu = Boolean(anchorEl);
	const navigate = useNavigate();
	const openSnackbar = useSnackbar();

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleClickOpenModal = () => {
		setOpen(true);
	};

	const handleCloseModal = () => {
		setOpen(false);
	};

	const editChatHandle = () => {
		navigate(`/club/${clubId}/chat/edit/${chatId}`);
	};

	const getChat = async () => {
		try {
			setLoading(true);
			if (chatId && matrixClient.isLoggedIn()) {
				const roomInfo = await matrixClient.getRoomInfo(chatId);
				setChat(roomInfo);
			}
		} catch (error) {
			console.log(error);
			setChat(null);
		}
		setLoading(false);
	};

	const deleteChat = async () => {
		try {
			setLoading(true);
			if (chatId) {
				await matrixClient.deleteRoom(chatId);
				openSnackbar("Chat deleted successfully");
				navigate(`/club`);
			}
		} catch (error) {
			console.log(error);
			openSnackbar("Error deleting chat");
		}
		setLoading(false);
	};

	useEffect(() => {
		if (matrixClient.isLoggedIn()) {
			getChat();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatId]);

	return (
		<Paper
			sx={{
				display: "flex",
				flexDirection: "column",
			}}
		>
			{loading && (
				<Box sx={{ width: "100%", my: 0.5 }}>
					<LinearProgress />
				</Box>
			)}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					paddingRight: "8px",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<IconButton
						onClick={() => navigate(`/club/${clubId || ""}`)}
						sx={{ ml: 1 }}
						size="small"
					>
						<ArrowBackIcon />
					</IconButton>
					<Typography
						component="h2"
						variant="h6"
						color="primary"
						sx={{
							px: 1,
							pt: 1,
							pb: 0,
							cursor: "pointer",
							"&:hover": { textDecoration: "underline" },
						}}
						onClick={() => navigate(`/club/${clubId}/chat/${encodeURIComponent(chatId || "")}/members`)}
					>
						{chat?.name || "Chat"}
					</Typography>
				</Box>
				<div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
					<IconButton
						color="primary"
						onClick={() => navigate(`/club/${clubId}/chat/${encodeURIComponent(chatId || "")}/members`)}
						title="Members"
					>
						<GroupIcon />
					</IconButton>
					<IconButton
						color="primary"
						onClick={() => setAddUserOpen(true)}
						title="Add User"
					>
						<PersonAddIcon />
					</IconButton>
					<IconButton
						aria-label="more"
						id="long-button"
						aria-controls={openMenu ? "long-menu" : undefined}
						aria-expanded={openMenu ? "true" : undefined}
						aria-haspopup="true"
						onClick={handleClick}
					>
						<MoreVertIcon />
					</IconButton>
					<Menu
						id="long-menu"
						MenuListProps={{
							"aria-labelledby": "long-button",
						}}
						anchorEl={anchorEl}
						open={openMenu}
						onClose={handleClose}
					>
						<MenuItem onClick={editChatHandle}>Edit Chat</MenuItem>
						<MenuItem onClick={handleClickOpenModal}>Delete Chat</MenuItem>
					</Menu>
				</div>
			</div>
			<div>
				{!!chatId ? (
					<MatrixProvider>
						<MatrixChatView roomId={chatId} />
					</MatrixProvider>
				) : (
					<Typography variant="body1" sx={{ p: 2 }}>Chat not found</Typography>
				)}
				<Dialog
					open={open}
					onClose={handleCloseModal}
					aria-labelledby="alert-dialog-title"
					aria-describedby="alert-dialog-description"
				>
					<DialogTitle id="alert-dialog-title">
						Are you sure you want to delete this chat?
					</DialogTitle>
					<DialogActions>
						<Button onClick={handleCloseModal} variant="outlined">
							Cancel
						</Button>
						<Button onClick={deleteChat} variant="contained" color="error">
							Delete
						</Button>
					</DialogActions>
				</Dialog>
				{chatId && clubId && (
					<AddUserToChatModalMatrix
						open={addUserOpen}
						onClose={() => setAddUserOpen(false)}
						chatId={chatId}
						clubId={clubId}
						onSuccess={() => getChat()}
					/>
				)}
			</div>
		</Paper>
	);
}
